import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FikoThreeJsComponent } from '../../fiko-three-js.component';
import { FikoYuzeyKalite } from './FikoYuzeyKalite';
import * as THREE from 'three';

/**
 * 🔺 CATIA-style Surface Finish Dialog
 */
@Component({
  selector: 'app-fiko-yuzey-kalite',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './fiko-yuzey-kalite.component.html',
  styleUrls: ['./fiko-yuzey-kalite.component.css']
})
export class FikoYuzeyKaliteComponent implements OnInit, OnDestroy {
  @Input() anaGosterim: FikoThreeJsComponent;
  @Input() pencere: any;

  // Surface finish settings
  raValue: number = 3.2;  // Ra (μm)
  islemTipi: string = 'machining';
  islemYonu: string = 'all';
  ekIslem: string = '';
  boyut: number = 12;
  renk: string = '#FFA500';

  // Process types
  islemTipleri = [
    { value: 'machining', label: 'Talaşlı İşlem (M)' },
    { value: 'grinding', label: 'Taşlama (G)' },
    { value: 'milling', label: 'Frezeleme (F)' },
    { value: 'turning', label: 'Tornalama (T)' },
    { value: 'polishing', label: 'Parlatma (P)' },
    { value: 'lapping', label: 'Rodlama (L)' },
    { value: 'honing', label: 'Honlama (H)' },
    { value: 'any', label: 'Herhangi' }
  ];

  // Lay direction
  islemYonleri = [
    { value: 'all', label: 'Tüm Yönler (=)' },
    { value: 'perpendicular', label: 'Dik (⊥)' },
    { value: 'parallel', label: 'Paralel (∥)' },
    { value: 'crossed', label: 'Çapraz (X)' },
    { value: 'radial', label: 'Radyal (R)' },
    { value: 'circular', label: 'Dairesel (C)' }
  ];

  // Common Ra values
  standartRaDegerleri = [0.1, 0.2, 0.4, 0.8, 1.6, 3.2, 6.3, 12.5, 25];

  // Point selection
  nokta: THREE.Vector3 | null = null;
  noktaSecimModu: boolean = false;

  // Current annotation
  mevcutYuzeyKalite: FikoYuzeyKalite | null = null;

  // Predefined colors
  renkler = ['#FFA500', '#FFD700', '#00FFFF', '#00FF00', '#FF00FF', '#FF4444'];

  // Sabit boyut - kamera zoom'una göre scale ayarlanır
  sabitBoyut: boolean = true;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  // Accordion states
  kaliteAyarlariAcik = true;
  islemAyarlariAcik = false;
  gorunumAyarlariAcik = false;

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.noktaSecimModu) {
      this.noktaSecimModunuKapat();
    }
  }

  /**
   * Start point selection mode
   */
  noktaSec(): void {
    this.noktaSecimModu = true;
    const canvas = this.anaGosterim?.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'crosshair';
      canvas.addEventListener('click', this.noktaTiklama);
    }
  }

  /**
   * Handle point click
   */
  private noktaTiklama = (event: MouseEvent) => {
    const position = this.get3DPositionFromMouse(event);
    if (!position) return;

    this.nokta = position;

    this.noktaSecimModunuKapat();
    this.onizlemeGuncelle();
  };

  /**
   * Close point selection mode
   */
  private noktaSecimModunuKapat(): void {
    this.noktaSecimModu = false;
    const canvas = this.anaGosterim?.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'default';
      canvas.removeEventListener('click', this.noktaTiklama);
    }
  }

  /**
   * Get 3D position from mouse
   */
  private get3DPositionFromMouse(event: MouseEvent): THREE.Vector3 | null {
    if (!this.anaGosterim?.sunucu || !this.anaGosterim?.aktifKamera) return null;

    const canvas = this.anaGosterim.sunucu.domElement;
    const rect = canvas.getBoundingClientRect();

    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.anaGosterim.aktifKamera);

    const sahne = this.anaGosterim.sahneler?.[0];
    if (!sahne) return null;

    const meshes = sahne.children.filter((obj: any) => obj.isMesh || obj.type === 'Mesh');
    const intersects = raycaster.intersectObjects(meshes, true);

    if (intersects.length > 0) {
      return intersects[0].point.clone();
    }

    // Fallback
    const distance = 5;
    const direction = new THREE.Vector3();
    this.anaGosterim.aktifKamera.getWorldDirection(direction);
    const position = this.anaGosterim.aktifKamera.position.clone();
    position.add(direction.multiplyScalar(distance));

    return position;
  }

  /**
   * Update preview
   */
  onizlemeGuncelle(): void {
    if (!this.nokta) return;

    const sahne = this.anaGosterim?.sahneler?.[0];
    if (!sahne) return;

    // Eski önizleme varsa sil
    if (this.mevcutYuzeyKalite) {
      sahne.remove(this.mevcutYuzeyKalite);
      this.mevcutYuzeyKalite.dispose();
    }

    // Yeni önizleme oluştur
    this.mevcutYuzeyKalite = new FikoYuzeyKalite(
      sahne,
      this.nokta,
      this.raValue,
      this.islemTipi
    );

    sahne.add(this.mevcutYuzeyKalite);
    this.anaGosterim?.ciz();
  }

  /**
   * Set Ra value from preset
   */
  setRaValue(value: number): void {
    this.raValue = value;
    this.onizlemeGuncelle();
  }

  /**
   * Toggle sabit boyut (fixed size regardless of zoom)
   */
  sabitBoyutDegistir(): void {
    if (this.mevcutYuzeyKalite) {
      this.mevcutYuzeyKalite.setSabitBoyut(this.sabitBoyut);
      this.anaGosterim?.ciz();
    }
  }

  /**
   * Add surface finish to scene
   */
  ekle(): void {
    if (!this.nokta) {
      alert('Lütfen önce bir nokta seçin!');
      return;
    }

    const sahne = this.anaGosterim?.sahneler?.[0];
    if (!sahne?.fikoEtiketler) return;

    // Create surface finish
    const yuzeyKalite = new FikoYuzeyKalite(
      sahne,
      this.nokta,
      this.raValue,
      this.islemTipi
    );

    // Add to etiketler group
    const etiket = sahne.fikoEtiketler.ekleYuzeyKalite();
    etiket.userData['FikoAnnotation'] = yuzeyKalite;
    yuzeyKalite.parent = etiket;
    sahne.add(yuzeyKalite);

    this.anaGosterim?.ciz();
    this.anaGosterim?.FikoYT?.degisti?.emit();

    // Reset
    this.nokta = null;
    if (this.mevcutYuzeyKalite) {
      sahne.remove(this.mevcutYuzeyKalite);
      this.mevcutYuzeyKalite = null;
    }
  }

  /**
   * Close dialog
   */
  kapat(): void {
    if (this.mevcutYuzeyKalite) {
      const sahne = this.anaGosterim?.sahneler?.[0];
      if (sahne) {
        sahne.remove(this.mevcutYuzeyKalite);
        this.mevcutYuzeyKalite.dispose();
      }
    }
    // pencere.instance → FikoDialogCore instance
    const dialogInstance = this.pencere?.instance ?? this.pencere;
    if (dialogInstance?.kapa) {
      dialogInstance.kapa();
    } else if (dialogInstance?.kapat) {
      dialogInstance.kapat();
    }
  }
}
